import type { ComponentDefinition } from "../types";
import type { Locale } from "./locales";
import { getCatalogText } from "./catalog-text";
import { getCatalogSearchIndex } from "./catalog-search";

type TranslatableField = "name" | "shortName" | "subtitle" | "description";

/**
 * On a generic (un-branded) component the text is *derived* from the live spec —
 * the first `Ah` token takes `capacityAh`, the first `W` takes `watts` — so
 * "200 Ah LiFePO4" stays honest after an edit. A branded entry keeps its name.
 */

const AH_TOKEN = /(\d+(?:\.\d+)?)\s*Ah/;
const W_TOKEN = /(\d+(?:\.\d+)?)\s*W(?![a-z])/;

function deriveText(text: string, def: ComponentDefinition): string {
  if (def.manufacturer || def.model || def.requestedModel) return text;
  let out = text;
  if (def.capacityAh != null) {
    out = out.replace(AH_TOKEN, (match) => `${def.capacityAh}${/\s/.test(match) ? " " : ""}Ah`);
  }
  if (def.watts != null) {
    out = out.replace(W_TOKEN, (match) => `${def.watts}${/\s/.test(match) ? " " : ""}W`);
  }
  return out;
}

function pickLocalized(
  locale: Locale,
  def: ComponentDefinition,
  field: TranslatableField,
): string | undefined {
  // A placeholder keeps its wiring-template id for reference, but none of the
  // template's names/descriptions are evidence about the requested model.
  if (def.requestedModel) return def[field];
  if (locale !== "en") {
    const translated = getCatalogText(locale)?.[def.id]?.[field];
    if (translated) return translated;
  }
  return def[field] ?? (field === "description" ? getCatalogText("en")?.[def.id]?.description : undefined);
}

export function localizedName(def: ComponentDefinition, locale: Locale): string {
  const raw = pickLocalized(locale, def, "name") ?? def.name;
  return deriveText(raw, def);
}

export function localizedShortName(
  def: ComponentDefinition,
  locale: Locale,
): string | undefined {
  const raw = pickLocalized(locale, def, "shortName");
  if (raw == null) return undefined;
  return deriveText(raw, def);
}

export function localizedSubtitle(
  def: ComponentDefinition,
  locale: Locale,
): string | undefined {
  const raw = pickLocalized(locale, def, "subtitle");
  if (raw == null) return undefined;
  return deriveText(raw, def);
}

export function localizedDescription(
  def: ComponentDefinition,
  locale: Locale,
): string | undefined {
  const raw = pickLocalized(locale, def, "description");
  if (raw == null) return undefined;
  return deriveText(raw, def);
}

/** All searchable text on a component, English plus every translation, so a
 *  Swedish user can still find a part by its English name. */
export function searchHaystack(def: ComponentDefinition): string[] {
  const out: (string | undefined)[] = [
    def.name,
    def.shortName,
    def.subtitle,
    def.description ?? (def.requestedModel ? undefined : getCatalogSearchIndex()?.[def.id]?.description),
    def.manufacturer,
    def.model,
    def.category,
  ];
  if (!def.requestedModel) out.push(...(getCatalogSearchIndex()?.[def.id]?.translations ?? []));
  return out.filter((s): s is string => typeof s === "string" && s.length > 0);
}

/** EVERY whitespace-separated token must appear somewhere in the haystack (AND
 *  semantics), so "orion xs 50" matches "Victron Orion XS 12/12-50". Matching is
 *  space-insensitive too: "100ah" finds "100 Ah LiFePO4". */
export function matchesSearch(
  haystack: string[],
  query: string,
  extra: string[] = [],
): boolean {
  const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return true;
  const joined = [...haystack, ...extra].join(" ").toLowerCase();
  const joinedNoSpace = joined.replace(/\s+/g, "");
  return tokens.every(
    (token) => joined.includes(token) || joinedNoSpace.includes(token),
  );
}
